import { useCallback, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  Button,
  FormControl,
  Grid2 as Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedTextFieldProps,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Organization } from '@/types';
import { useAppDispatch } from '@/hooks';
import { updateOrganization } from '@/features/organizations/slice';
import { OrganizationContext } from '@/features/organizations/components/OrganizationLayout';
import Map, { INITIAL_LOCATION } from '@/components/map/Map';
import { industries, Industry } from '@/constants/industries';
import * as Yup from 'yup';
import PageHeader from '@/components/PageHeader';
import {
  Facebook,
  Instagram,
  MailOpen,
  MessageCircle,
  Phone,
} from 'lucide-react';
import InputMask from 'react-input-mask';
import useSnackbar from '@/hooks/useSnackbar';
import { useAccessControl, withAccessControl } from '@/features/access-control';
import { sanitizePhone } from '@/utils/phones';

type FormValues = Pick<
  Organization,
  | 'id'
  | 'title'
  | 'slogan'
  | 'industry'
  | 'email'
  | 'address'
  | 'latitude'
  | 'longitude'
  | 'website'
  | 'domain'
  | 'phone'
  | 'whatsapp'
  | 'facebook'
  | 'instagram'
>;

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .strict(true)
    .min(4, 'Muy Corto!')
    .max(50, 'Muy Largo!')
    .required('Campo requerido'),
  slogan: Yup.string(),
  domain: Yup.string(),
  address: Yup.string()
    .strict(true)
    .required('Ingresa tu dirección, para que tus clientes te encuentren'),
  phone: Yup.string()
    .min(8, 'Muy Corto!')
    .max(8, 'Muy Largo!')
    .required('Campo requerido'),
  whatsapp: Yup.string()
    .min(8, 'Muy Corto!')
    .max(8, 'Muy Largo!')
    .required('Campo requerido'),
  facebook: Yup.string(),
  instagram: Yup.string(),
  email: Yup.string()
    .strict(true)
    .email('Correo inválido')
    .required('Campo requerido'),
});

const OrganizationDetails = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { currentOrganization } = useOutletContext<OrganizationContext>();

  const sharedInputProps = useMemo(() => {
    return {
      size: 'small',
      fullWidth: true,
      margin: 'normal',
      disabled: !hasAccess('update.organization'),
    } as Pick<
      OutlinedTextFieldProps,
      'size' | 'fullWidth' | 'disabled' | 'margin'
    >;
  }, [hasAccess]);

  const initialValues = {
    id: '',
    title: '',
    slogan: '',
    industry: '',
    website: '',
    domain: '',
    email: '',
    address: '',
    phone: '',
    whatsapp: '',
    latitude: INITIAL_LOCATION.lat,
    longitude: INITIAL_LOCATION.lng,
    facebook: '',
    instagram: '',
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values: FormValues) => {
      try {
        await dispatch(updateOrganization({ ...values }));
        openSnackbar('Datos actualizados', 'success');
      } catch (error) {
        console.error(error);
        openSnackbar('Error al actualizar los datos', 'error');
      }
    },
    validationSchema,
    enableReinitialize: true,
  });

  const loadMap = useCallback(
    (map: any, maps: any) => {
      const position = {
        lat: formik.values.latitude ?? INITIAL_LOCATION.lat,
        lng: formik.values.longitude ?? INITIAL_LOCATION.lng,
      };
      const marker = new maps.Marker({
        position,
        map,
        draggable: true,
      });

      if (hasAccess('update.organization')) {
        marker.addListener('dragend', () => {
          const newLatitude = marker.getPosition().lat();
          const newLongitude = marker.getPosition().lng();

          formik.setFieldValue('latitude', newLatitude);
          formik.setFieldValue('longitude', newLongitude);
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.latitude, formik.values.longitude, currentOrganization]
  );

  useEffect(() => {
    if (currentOrganization) {
      const {
        id,
        title,
        slogan,
        industry,
        website,
        domain,
        email,
        address,
        phone,
        whatsapp,
        facebook,
        instagram,
        latitude,
        longitude,
      } = currentOrganization;

      formik.setValues({
        ...formik.values,
        id: id,
        title: title ?? '',
        slogan: slogan ?? '',
        industry: industry ?? '',
        website: website ?? '',
        domain: domain ?? '',
        email: email ?? '',
        address: address ?? '',
        phone: phone ?? '',
        whatsapp: whatsapp ?? '',
        facebook: facebook ?? '',
        instagram: instagram ?? '',
        latitude: latitude ?? INITIAL_LOCATION.lat,
        longitude: longitude ?? INITIAL_LOCATION.lng,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization]);

  if (!currentOrganization) return null;

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Datos del comercio"
        action={
          hasAccess('update.organization') ? (
            <Button
              size="large"
              color="success"
              type="submit"
              fullWidth={false}
              onClick={() => formik.handleSubmit()}
            >
              Guardar
            </Button>
          ) : undefined
        }
      />

      <Paper sx={{ padding: 2 }}>
        <Stack gap={2}>
          <Typography variant="h4">Generales</Typography>
          <Stack>
            <Stack direction={'row'} spacing={2}>
              <TextField
                label="Título"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={!!formik.touched.title && !!formik.errors?.title}
                helperText={formik.touched.title && formik.errors?.title}
                {...sharedInputProps}
              />

              <FormControl fullWidth {...sharedInputProps}>
                <InputLabel id="industry-id">Industria</InputLabel>
                <Select
                  labelId="industry-id"
                  label="Industria"
                  name="industry"
                  value={formik.values.industry ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.industry && !!formik.errors?.industry}
                >
                  {Object.values(industries).map((industry: Industry) => (
                    <MenuItem key={industry.id} value={industry.id}>
                      {industry.locale.es}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="Eslogan"
              name="slogan"
              value={formik.values.slogan}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!formik.touched.slogan && !!formik.errors?.slogan}
              helperText={formik.touched.slogan && formik.errors?.slogan}
              {...sharedInputProps}
            />
          </Stack>
        </Stack>
      </Paper>

      <Grid container direction={'row'} spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ padding: 2 }}>
            <Stack gap={2}>
              <Typography variant="h4">Contacto</Typography>
              <Stack>
                <Stack
                  direction={'row'}
                  sx={{
                    gap: '16px',
                  }}
                >
                  <InputMask
                    mask="9999-9999"
                    maskChar={null}
                    placeholder="0000-0000"
                    alwaysShowMask
                    value={formik.values.whatsapp}
                    onChange={(e) => {
                      formik.setFieldValue('whatsapp', sanitizePhone(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    disabled={sharedInputProps.disabled}
                  >
                    {() => (
                      <TextField
                        label="Whatsapp"
                        name="whatsapp"
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <MessageCircle />
                              </InputAdornment>
                            ),
                          },
                        }}
                        error={
                          !!formik.touched.whatsapp && !!formik.errors?.whatsapp
                        }
                        helperText={
                          formik.touched.whatsapp && formik.errors?.whatsapp
                        }
                        {...sharedInputProps}
                      />
                    )}
                  </InputMask>

                  <InputMask
                    mask="9999-9999"
                    maskChar={null}
                    placeholder="0000-0000"
                    alwaysShowMask
                    value={formik.values.phone}
                    onChange={(e) => {
                      formik.setFieldValue('phone', sanitizePhone(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    disabled={sharedInputProps.disabled}
                  >
                    {() => (
                      <TextField
                        label="Teléfono"
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone />
                              </InputAdornment>
                            ),
                          },
                        }}
                        name="phone"
                        error={!!formik.touched.phone && !!formik.errors?.phone}
                        helperText={
                          formik.touched.phone && formik.errors?.phone
                        }
                        {...sharedInputProps}
                      />
                    )}
                  </InputMask>
                </Stack>

                <TextField
                  label="Email"
                  type="email"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOpen />
                        </InputAdornment>
                      ),
                    },
                  }}
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.email && !!formik.errors?.email}
                  helperText={formik.touched.email && formik.errors?.email}
                  {...sharedInputProps}
                />
                <TextField
                  label="Sitio web"
                  name="domain"
                  value={formik.values.domain}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.domain && !!formik.errors?.domain}
                  helperText={formik.touched.domain && formik.errors?.domain}
                  {...sharedInputProps}
                />

                <Map
                  height="250px"
                  googleApiLoaded={({ map, maps }) => loadMap(map, maps)}
                  type="satellite"
                />

                <TextField
                  label="Dirección"
                  multiline
                  rows={3}
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.address && !!formik.errors?.address}
                  helperText={formik.touched.address && formik.errors?.address}
                  {...sharedInputProps}
                />
              </Stack>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} mb={3}>
          <Paper sx={{ padding: 2 }}>
            <Stack gap={2}>
              <Typography variant="h4">Redes Sociales</Typography>
              <Stack>
                <TextField
                  label="Facebook"
                  type="url"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Facebook />
                        </InputAdornment>
                      ),
                    },
                  }}
                  name="facebook"
                  value={formik.values.facebook}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.facebook && !!formik.errors?.facebook}
                  helperText={
                    formik.touched.facebook && formik.errors?.facebook
                  }
                  {...sharedInputProps}
                />
                <TextField
                  label="Instagram"
                  type="url"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Instagram />
                        </InputAdornment>
                      ),
                    },
                  }}
                  name="instagram"
                  value={formik.values.instagram}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    !!formik.touched.instagram && !!formik.errors?.instagram
                  }
                  helperText={
                    formik.touched.instagram && formik.errors?.instagram
                  }
                  {...sharedInputProps}
                />
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default withAccessControl(OrganizationDetails, 'view.organization');
